import { h, Fragment } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import Flyout from 'utils/flyout';
import Panel from 'utils/panel';
import {
  formatMoney,
  formatCartLinesAjax,
} from 'utils/storefront/format-resource';
import { IconClose, IconShippingAlert, IconChecked } from 'utils/icon';
import useWindowSize from 'utils/useWindowSize';
import getBestLoyaltyRedemption from 'utils/get-best-loyalty-redemption';
import CartItem from './cart-item';
import { withCartContext } from './cart-context';
import CartPageItem from './cart-page-item';
import './cart.css';
import CartGiftFlyout from './cart-gift-flyout';
import CartSkeleton from './cart-skeleton';
import MiniCartSkeleton from './mini-cart-skeleton';

const Cart = (props) => {
  const { cart, cartLoading, data } = props;
  const { isTablet, isMobile } = useWindowSize();
  const lines =
    cart?.mode === 'storefront'
      ? cart?.state?.lines
      : formatCartLinesAjax(cart?.state?.items);

  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: null,
    closeTriggerEl: null,
    toggleEl: null,
  });

  if (data.enableMiniCart) {
    useEffect(() => {
      const toggleEl = document.querySelector('#miniCartToggle');
      if (toggleEl) {
        setPanelTriggers((prev) => ({
          ...prev,
          toggleEl,
        }));
      }
    }, []);
  }

  lines.forEach((item) => {
    if (item.handle === 'sustainability-coverage') {
      cart.remove([item.id]);
    }
  });

  const countNoChargeProducts = lines.reduce((acc, item) => {
    if (
      item.product_type.toLowerCase() !== 'charge' &&
      item.handle.toLowerCase() !== 'sustainability-coverage'
    ) {
      return acc + (item.vendor === 'DailyKarma' ? 1 : item.quantity);
    }
    return acc;
  }, 0);

  if (window.location.pathname === '/cart') {
    return (
      <CartPageContent
        cart={cart}
        lines={lines}
        data={data}
        itemCountNoChargeProducts={countNoChargeProducts}
      ></CartPageContent>
    );
  }

  const filteredLines = lines?.filter((line) => line);
  const linesWithChargeRemoved = filteredLines?.filter(
    (line) => line.product_type.toLowerCase() !== 'charge',
  );
  const isTouch = isMobile || isTablet;
  const components = {
    panel: (
      <Panel
        alignment="bottom"
        triggers={panelTriggers}
        bodyScrollLock={true}
        context="cart"
        zIndex={'z-[49]'}
        duration={3000}
        classes={'max-h-none !h-auto max-w-none'}
      >
        <CartContent
          cart={cart}
          data={data}
          setPanelTriggers={setPanelTriggers}
          isTablet={isTablet}
          isMobile={isMobile}
          itemCountNoChargeProducts={countNoChargeProducts}
          lines={linesWithChargeRemoved}
        />
      </Panel>
    ),
    flyout: (
      <Flyout
        triggers={panelTriggers}
        classes="shadow-[0_0_4px_4px_rgba(0,0,0,0.04)]"
        duration={3000}
        context="cart"
      >
        <CartContent
          cart={cart}
          data={data}
          setPanelTriggers={setPanelTriggers}
          isTablet={isTablet}
          isMobile={isMobile}
          itemCountNoChargeProducts={countNoChargeProducts}
          lines={linesWithChargeRemoved}
        />
      </Flyout>
    ),
    miniCartPanel: (
      <Panel
        alignment={isTouch ? 'bottom' : 'right'}
        triggers={panelTriggers}
        bodyScrollLock={true}
        context="cart"
        zIndex={'z-[49]'}
        classes="max-h-[calc(100vh-104px)] max-w-none lg:max-h-screen lg:max-w-[500px]"
        overlay={true}
        overlayClasses="!bg-black !opacity-60"
      >
        <CartContent
          cart={cart}
          data={data}
          setPanelTriggers={setPanelTriggers}
          isTablet={isTablet}
          isMobile={isMobile}
          itemCountNoChargeProducts={countNoChargeProducts}
          lines={linesWithChargeRemoved}
          isLoading={cartLoading}
        />
      </Panel>
    ),
  };

  if (data.enableMiniCart) {
    return components.miniCartPanel;
  }
  return isTouch ? components.panel : components.flyout;
};

const CartContent = ({
  cart,
  data,
  lines,
  setPanelTriggers,
  itemCountNoChargeProducts,
  isLoading,
}) => {
  const {
    bestSellersLink,
    emptyCartButtonText,
    enableMiniCart: isMiniCartEnabled,
    enableShippingMessage,
  } = data;
  const closeTriggerEl = useRef(null);
  const desktopDynamicCheckoutRef = useRef(null);
  const [lastUpdatedItem, setLastUpdatedItem] = useState(null);
  const previousLinesRef = useRef([]);
  const { isTablet, isMobile } = useWindowSize();
  const isCartEmpty = cart?.state?.item_count === 0;

  useEffect(() => {
    let isFetching = false;

    const fetchDynamicCheckoutButtons = async () => {
      // Check if the dynamic checkout buttons are already present
      if (
        desktopDynamicCheckoutRef.current &&
        desktopDynamicCheckoutRef.current.children.length > 0
      ) {
        return;
      }

      if (isFetching) {
        return;
      }

      isFetching = true;

      try {
        const response = await fetch('/cart?section_id=cart');
        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const dynamicCheckoutContainer = doc.querySelector(
          '[data-dynamic-checkout-container]',
        );
        if (!dynamicCheckoutContainer || !desktopDynamicCheckoutRef.current)
          return;
        desktopDynamicCheckoutRef.current.innerHTML = ''; // Clear previous content
        desktopDynamicCheckoutRef.current.appendChild(
          dynamicCheckoutContainer.children[0],
        );
      } finally {
        isFetching = false;
      }
    };

    fetchDynamicCheckoutButtons();

    return () => {
      if (desktopDynamicCheckoutRef.current) {
        desktopDynamicCheckoutRef.current.innerHTML = '';
      }
    };
  }, [cart.state.item_count]);

  useEffect(() => {
    const previousLines = previousLinesRef.current;

    if (previousLines.length > 0) {
      const updatedItem = lines.find((newLine, index) => {
        const prevLine = previousLines[index];
        return !prevLine || newLine.quantity !== prevLine.quantity;
      });

      if (
        lastUpdatedItem &&
        !lines.find((line) => line.key === lastUpdatedItem.key)
      ) {
        setLastUpdatedItem(null);
      }

      if (updatedItem) {
        setLastUpdatedItem(updatedItem);
      }
    }

    previousLinesRef.current = lines;

    if (window.Nosto && typeof window.Nosto.reloadCart === 'function') {
      window.Nosto.reloadCart();
    }
  }, [lines, lastUpdatedItem]);

  useEffect(() => {
    if (!isLoading) {
      if (window.Nosto && typeof window.Nosto.reloadCart === 'function') {
        window.Nosto.reloadCart();
      }
    }
  }, [isLoading]);
  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  const remainingItems = lines.filter(
    (line) => line && (!lastUpdatedItem || line.key !== lastUpdatedItem.key),
  );

  // Check for cart level discounts
  const cartDiscounts = cart?.state?.cart_level_discount_applications || [];
  const configuredGiftProduct = data.giftProduct && data.giftProduct[0];
  const technicalDataEl = document.querySelector('[data-technical]');
  const technicalData =
    technicalDataEl?.innerHTML && JSON.parse(technicalDataEl.innerHTML);
  const isLoggedIn = technicalData?.customer;

  const els = {
    default: {
      header: (
        <div className="flex justify-between p-5 bg-core-0 relative lg:border-b lg:border-gray-300">
          <h4 className="h4 capitalize hidden lg:block">
            {window.TMW.Strings.cart.cart}&nbsp;(&nbsp;
            {itemCountNoChargeProducts}&nbsp;)
          </h4>
          <h4 className="h4 justify-center flex capitalize w-full lg:hidden">
            <IconChecked />
            &nbsp;{window.TMW.Strings.cart.itemAdded}
          </h4>
          <button
            type="button"
            className="absolute right-5 top-6 lg:static"
            ref={closeTriggerEl}
          >
            <IconClose />
          </button>
        </div>
      ),
      lineItems: (
        <div className="cart-popup mx-5 py-5 lg:pb-0 lg:scrollbar-track:my-3.5 lg:scrollbar:w-0.5 lg:scrollbar:rounded-[20px] lg:scrollbar-thumb:rounded-[20px] lg:scrollbar-track:bg-gray-400 lg:scrollbar-thumb:bg-gray-700 lg:h-[414px] lg:overflow-y-scroll lg:pr-6 flex-grow bg-core-0 lg:bg-white">
          {lastUpdatedItem && (isMobile || isTablet) ? (
            <CartItem key={lastUpdatedItem.key} line={lastUpdatedItem} />
          ) : (
            <div>
              {lastUpdatedItem && (
                <CartItem key={lastUpdatedItem.key} line={lastUpdatedItem} />
              )}

              {remainingItems?.length > 0 && (
                <div>
                  {remainingItems.map((line) => (
                    <CartItem key={line.key} line={line} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ),
      subtotal: (
        <div className="flex justify-between h7--medium mb-4 lg:mb-1">
          <p className="lg:hidden">{itemCountNoChargeProducts}&nbsp;items</p>
          <p className="flex gap-3 lg:justify-between lg:w-full capitalize body-2--medium">
            <span className="">{window.TMW.Strings.cart.subtotal}&nbsp;</span>
            {cart.mode === 'storefront'
              ? formatMoney(
                  (cart?.state?.subtotal && {
                    amount: cart.state.subtotal,
                  }) || { amount: 0 },
                )
              : formatMoney(
                  cart?.state?.total_price
                    ? { amount: cart.state.total_price }
                    : { amount: 0 },
                  true,
                )}
          </p>
        </div>
      ),
    },
    miniCart: {
      close: (
        <button
          type="button"
          ref={closeTriggerEl}
          className="flex justify-between p-[10px] border-gray-400 border-b bg-white"
          aria-label={window.TMW.Strings.cart.continue_shopping}
        >
          <p className="body-3 underline underline-offset-[3px] text-default">
            {window.TMW.Strings.cart.continue_shopping}
          </p>
          <IconClose aria-hidden="true" />
        </button>
      ),
      header: (
        <div className="p-5 bg-core-1 lg:border-b lg:border-gray-300">
          <h4 className="h4 justify-center flex capitalize w-full text-center">
            {window.TMW.Strings.cart.cart}
          </h4>
        </div>
      ),
      lineItems: (
        <div>
          {lines?.length > 0 && (
            <div>
              {lines.map((line) => (
                <CartPageItem
                  key={line.key}
                  line={line}
                  isMiniCart={true}
                  monogramProduct={
                    data?.monogramProduct && data?.monogramProduct[0]
                  }
                  enableFreeMonogramForVIP={data.enableFreeMonogramForVIP}
                  configuredGiftProduct={configuredGiftProduct}
                />
              ))}
            </div>
          )}
        </div>
      ),
      activeCoupons: (
        <div className="my-[20px]">
          <div className="bg-core-0 text-center px-[20px] py-[10px]">
            {cartDiscounts?.length ? (
              cartDiscounts?.map((discount, index) => (
                <p
                  key={index}
                  className="text-[14px] leading-[140%] tracking-[0.025em] font-medium"
                >
                  {discount.title} applied
                </p>
              ))
            ) : (
              <p className="text-[14px] font-normal leading-[140%] tracking-[0.025em]">
                {window.TMW.Strings.cart.promo}
              </p>
            )}
          </div>
        </div>
      ),
      emptyCart: (
        <div className="bg-core-0 py-[56px] grid grid-cols-1 text-center">
          <h2 className="capitalize text-2xl font-normal leading-[1.1] mb-[20px] text-center max-w-[192px] mx-auto inline-block">
            {window.TMW.Strings.cart.emptyBag}
          </h2>
          {emptyCartButtonText && (
            <a
              href={bestSellersLink}
              className="button button--primary no-underline !font-medium uppercase w-full max-w-[200px] mx-auto !px-[20px] !py-[12px]"
            >
              {emptyCartButtonText}
            </a>
          )}
        </div>
      ),
      subtotal: (
        <p className="flex gap-3 justify-between w-full capitalize body-2--medium mb-[10px]">
          <span>
            {window.TMW.Strings.cart.subtotal}&nbsp;
            <span className="font-normal">
              ({itemCountNoChargeProducts}&nbsp;items)
            </span>
          </span>
          {cart.mode === 'storefront'
            ? formatMoney(
                (cart?.state?.subtotal && {
                  amount: cart.state.subtotal,
                }) || { amount: 0 },
              )
            : formatMoney(
                cart?.state?.total_price
                  ? { amount: cart.state.total_price }
                  : { amount: 0 },
                true,
              )}
        </p>
      ),
      freeShippingFooter: (
        <p className="ge-hide-display-none flex gap-1.5 text-sm justify-center text-center mt-[5px] -mb-[12px]">
          <IconShippingAlert />
          {window.TMW.Strings.cart.shippingMessage}
        </p>
      ),
      dynamicCheckout: (
        <div className="dynamic_checkout empty:hidden p-5 -mx-5 -mb-5 bg-core-1 border-t lg:border-none border-gray-400 ge-hide-display-none">
          <h2 className="body-1--medium text-center md:text-left mb-[6px]">
            {window.TMW.Strings.cart.expressCheckout}
          </h2>
          <div
            className="dynamic-checkout__buttons"
            ref={desktopDynamicCheckoutRef}
          ></div>
        </div>
      ),
    },
  };

  return (
    <div className="w-full h-full flex flex-col bg-core-0 md:align-start md:bg-white">
      {isMiniCartEnabled ? (
        <Fragment>
          {els.miniCart.close}
          {els.miniCart.header}
          {isLoading ? (
            <MiniCartSkeleton />
          ) : (
            <div
              className={`cart-popup scrollbar-track:my-3.5 scrollbar:w-0.5
                  scrollbar:rounded-[20px] scrollbar-thumb:rounded-[20px]
                  scrollbar-track:bg-gray-400 scrollbar-thumb:bg-gray-700
                  lg:h-[414px] overflow-y-scroll flex-grow overflow-x-hidden
                  max-h-[calc(100%-170px)] lg:max-h-none
                  ${isCartEmpty ? 'bg-core-0' : 'bg-white'}`}
            >
              {isCartEmpty ? (
                els.miniCart.emptyCart
              ) : (
                <div className="p-5">
                  {els.miniCart.lineItems}
                  {els.miniCart.activeCoupons}
                  {isLoggedIn && data.enableRewardsMessaging && (
                    <div className="-mx-5">
                      <LoyaltyPoints isMiniCartEnabled={isMiniCartEnabled} />
                    </div>
                  )}
                  {els.miniCart.dynamicCheckout}
                </div>
              )}
            </div>
          )}
        </Fragment>
      ) : (
        <Fragment>
          {els.default.header}
          {els.default.lineItems}
        </Fragment>
      )}
      <CartGiftFlyout
        isMiniCartEnabled={true}
        cart={cart}
        giftProduct={data?.giftProduct && data?.giftProduct[0]}
      />
      <div
        className={`${
          isMiniCartEnabled ? 'bg-core-1' : 'bg-core-0'
        } pt-4 px-5 pb-5 border-t lg:border-none border-gray-400`}
      >
        {isMiniCartEnabled ? (
          els.miniCart.subtotal
        ) : (
          <Fragment>
            {els.default.subtotal}
            <div className="justify-between hidden lg:flex mb-4 ge-hide-display-none">
              <p>{window.TMW.Strings.cart.shipping}</p>
              <p className="flex gap-1.5">
                <IconShippingAlert />
                {window.TMW.Strings.cart.free}
              </p>
            </div>
          </Fragment>
        )}

        <a
          className="justify-center w-full button button--primary no-underline"
          href={`${window.Shopify.routes.root}checkout`}
        >
          {window.TMW.Strings.cart.checkout}
        </a>
        {isMiniCartEnabled &&
          enableShippingMessage &&
          els.miniCart.freeShippingFooter}
      </div>
    </div>
  );
};

const CartPageContent = ({ cart, lines, data, itemCountNoChargeProducts }) => {
  const { isMobile } = useWindowSize();
  const desktopDynamicCheckoutRef = useRef(null);
  const mobileDynamicCheckoutRef = useRef(null);
  const technicalDataEl = document.querySelector('[data-technical]');
  const { bestSellersLink, emptyCartButtonText } = data;
  const configuredGiftProduct = data.giftProduct && data.giftProduct[0];
  const giftWrapLines = lines.filter(
    (line) => line.properties?._charge_type === 'gift',
  );
  const technicalData =
    technicalDataEl?.innerHTML && JSON.parse(technicalDataEl.innerHTML);
  const isLoggedIn = technicalData?.customer;

  const linesWithChargeRemoved = lines.filter(
    (item) =>
      item.product_type !== 'Charge' &&
      item.handle !== 'sustainability-coverage',
  );

  useEffect(() => {
    setTimeout(() => {
      const dynamicCheckoutContainer = document.querySelector(
        '[data-dynamic-checkout-container]',
      );
      if (!dynamicCheckoutContainer) return;

      if (isMobile && mobileDynamicCheckoutRef.current) {
        mobileDynamicCheckoutRef.current.appendChild(
          dynamicCheckoutContainer.children[0],
        );
      } else if (!isMobile && desktopDynamicCheckoutRef.current) {
        desktopDynamicCheckoutRef.current.appendChild(
          dynamicCheckoutContainer.children[0],
        );
      }
    }, 500);
  }, [isMobile]);

  const [isLoading, setIsLoading] = useState(true);
  const isCartEmpty = cart?.state?.item_count === 0;

  useEffect(() => {
    if (cart?.state?.item_count !== undefined) {
      setIsLoading(false);

      if (document.querySelector('.cart-not-loaded')) {
        document
          .querySelector('[data-section="cart"]')
          .classList.remove('cart-not-loaded');
      }
    }
  }, [cart?.state?.item_count]);

  if (isLoading) return <CartSkeleton />;

  return !isCartEmpty ? (
    <div className="w-full h-full flex flex-col justify-between md:flex-row md:items-start">
      <CartGiftFlyout
        cart={cart}
        giftProduct={data.giftProduct && data.giftProduct[0]}
      />
      <div className="mt-5 mb-5 mx-5 md:mr-0 md:ml-10 md:mt-16 md:mb-0 md:w-[56%]">
        <h2 className="capitalize md:mb-5">
          {window.TMW.Strings.cart.cart}&nbsp;(&nbsp;
          {itemCountNoChargeProducts}
          &nbsp;)
        </h2>
        {linesWithChargeRemoved?.map(
          (line) =>
            line && (
              <CartPageItem
                key={line?.key}
                monogramProduct={
                  data?.monogramProduct && data?.monogramProduct[0]
                }
                line={line}
                configuredGiftProduct={configuredGiftProduct}
                giftWrapLines={giftWrapLines}
              />
            ),
        )}
      </div>
      {/* Desktop summary view */}
      <div className="desktop_summary hidden w-[32%] p-10 mt-12 mr-5 md:mr-10 md:flex flex-col border-background-3 border-solid border">
        <h2 className="capitalize mb-7">
          {window.TMW.Strings.cart.orderSummary}
        </h2>
        <div className="desktop_order_details flex flex-col gap-2">
          <div className="totals bg-core-0 flex flex-col px-5 py-5 sticky bottom-0">
            <div className="flex justify-between mb-2.5">
              <p className="capitalize text-sm font-medium">
                {window.TMW.Strings.cart.subtotal}&nbsp;
              </p>
              <p className="flex gap-3 text-sm font-medium">
                {cart.mode === 'storefront'
                  ? formatMoney(
                      (cart?.state?.subtotal && {
                        amount: cart.state.subtotal,
                      }) || { amount: 0 },
                    )
                  : formatMoney(
                      cart?.state?.total_price
                        ? { amount: cart.state.total_price }
                        : { amount: 0 },
                      true,
                    )}
              </p>
            </div>
            <div className="flex justify-between mb-2.5 ge-hide-display-none">
              <p className="text-sm">{window.TMW.Strings.cart.shipping}</p>
              <p className="flex gap-1.5 text-sm">
                <IconShippingAlert className="" />
                {window.TMW.Strings.cart.free}
              </p>
            </div>
            <div className="flex justify-between mb-2.5">
              <p className="capitalize text-sm">
                {window.TMW.Strings.cart.taxes}&nbsp;
              </p>
              <p className="capitalize text-sm">
                {window.TMW.Strings.cart.calculatedAtCheckout}
              </p>
            </div>
            <a
              className="justify-center w-full button button--primary no-underline"
              href={`${Shopify.routes.root}checkout`}
            >
              {window.TMW.Strings.cart.checkout}
            </a>
          </div>
          {isLoggedIn && data.enableRewardsMessaging && <LoyaltyPoints />}
          <p className="px-5 py-2.5 text-sm bg-core-0 text-black">
            {window.TMW.Strings.cart.promo}
          </p>
          <div className="dynamic_checkout empty:hidden p-5 bg-core-0 ge-hide-display-none">
            <h2 className="body-1--medium text-center mb-[6px]">
              {window.TMW.Strings.cart.expressCheckout}
            </h2>
            <div
              className="dynamic-checkout__buttons"
              ref={desktopDynamicCheckoutRef}
            ></div>
          </div>
        </div>
      </div>
      {/* Mobile summary stack */}
      <div className="totals bg-core-0 flex flex-col px-5 py-5 sticky bottom-0 md:hidden">
        <div className="flex justify-between mb-4 lg:mb-1">
          <p className="capitalize text-sm font-medium">
            {window.TMW.Strings.cart.subtotal}&nbsp;
          </p>
          <p className="flex gap-3 text-sm font-medium">
            {cart.mode === 'storefront'
              ? formatMoney(
                  (cart?.state?.subtotal && {
                    amount: cart.state.subtotal,
                  }) || { amount: 0 },
                )
              : formatMoney(
                  cart?.state?.total_price
                    ? { amount: cart.state.total_price }
                    : { amount: 0 },
                  true,
                )}
          </p>
        </div>
        <div className="flex justify-between mb-4 ge-hide-display-none">
          <p className="text-sm">{window.TMW.Strings.cart.shipping}</p>
          <p className="flex gap-1.5 text-sm">
            <IconShippingAlert className="hidden md:block" />
            {window.TMW.Strings.cart.free}
          </p>
        </div>
        <div className="flex justify-between mb-4 lg:mb-1">
          <p className="capitalize text-sm">
            {window.TMW.Strings.cart.taxes}&nbsp;
          </p>
          <p className="capitalize text-sm">
            {window.TMW.Strings.cart.calculatedAtCheckout}&nbsp;
          </p>
        </div>
        <a
          className="justify-center w-full button button--primary no-underline"
          href="/checkout"
        >
          {window.TMW.Strings.cart.checkout}
        </a>
      </div>

      {isLoggedIn && data.enableRewardsMessaging && (
        <div className="md:hidden mx-5 mt-2.5">
          <LoyaltyPoints />
        </div>
      )}

      <div className="px-5 py-[30px] bg-white md:hidden">
        <p className="px-5 py-2.5 text-sm bg-core-0 text-black">
          {window.TMW.Strings.cart.promo}
        </p>
      </div>
      <div className="dynamic_checkout empty:hidden p-5 bg-core-0 md:hidden">
        <h2 className="body-1--medium text-center mb-[6px]">
          {window.TMW.Strings.cart.expressCheckout}
        </h2>
        <div
          className="dynamic-checkout__buttons"
          ref={mobileDynamicCheckoutRef}
        ></div>
      </div>
    </div>
  ) : (
    <div className="lg:px-[40px] lg:pt-[43px]">
      <div className="flex flex-col justify-center items-center bg-core-0 min-h-[314px] lg:min-h-[301px]">
        <h2 className="pb-[20px] max-w-[192px] text-center lg:max-w-fit lg:pb-[24px]">
          {window.TMW.Strings.cart.emptyBag}
        </h2>
        {emptyCartButtonText && (
          <a
            href={bestSellersLink}
            className="button button--primary no-underline !font-medium uppercase"
          >
            {emptyCartButtonText}
          </a>
        )}
      </div>
    </div>
  );
};

const LoyaltyPoints = ({ isMiniCartEnabled = false }) => {
  if (
    !window.TMW.Strings.cart.rewardsMessage ||
    window.Shopify.country !== 'US'
  )
    return null;
  const messageRef = useRef();

  const [message, setMessage] = useState(
    window.TMW.Strings.cart.rewardsMessage,
  );

  useEffect(async () => {
    const {
      currentRedemptionOption,
      nextRedemptionOption,
      isFirstOption,
      pointsBalance,
    } = await getBestLoyaltyRedemption();

    if (isFirstOption) {
      const newMessage = window.TMW.Strings.cart.noRewardsMessage.replace(
        '{XXX}',
        nextRedemptionOption.costInPoints - pointsBalance,
      );
      setMessage(newMessage);
      messageRef.current.classList.remove('hidden');
    } else {
      const newMessage = message
        .replace(
          '{money}',
          `<span class='font-medium'>
            ${currentRedemptionOption.name}
          </span>`,
        )
        .replace('{points}', currentRedemptionOption.costInPoints);
      setMessage(newMessage);

      messageRef.current.classList.remove('hidden');
    }
  }, []);

  return (
    <div
      className={`
        text-text-default bg-core-6 px-5 py-2.5 lg:py-5 text-center hidden ${
          !isMiniCartEnabled ? 'lg:text-left' : ''
        }`}
      ref={messageRef}
      data-ge-hidden
    >
      <p
        className="body-2 mb-1.5 lg:mb-0.5 test"
        dangerouslySetInnerHTML={{ __html: message }}
      />
      <p className="body-4">{window.TMW.Strings.cart.rewardsSubtitle}</p>
    </div>
  );
};

export default withCartContext(Cart);
